import { all, call, cancel, put, select } from 'redux-saga/effects';

import { getOrderSummary } from 'features/order-tracker/api/orderTrackerApi';

import {
  addMilestone,
  fetchGroupOrder,
  fetchOrderSummarySucceed,
  setAPIFailed,
  setCachedDataToStore,
  setDataToStore,
  setOrderStatusFromOrderSummary,
} from '../../actions';
import { getStatuses } from '../../selectors';
import { getServiceActivationMilestones, isDateInFuture } from '../services/helpers/serviceActivationFormatter';
import { OrderSummaryService } from '../services/orderSummaryService';

function* putOrderSummaryDataToStore(responses: transformedResponse.OrderSummary[], placedOn: string): Generator {
  const {
    getSortedProducts,
    getOrderSummaryStatus,
    getOrderDescription,
    getSubOrdersStatuses,
    getOrderPlacedMilestone,
    getProductsInformation,
    getBillingMilestone,
    getHighDemandEngineerVisitsBanner,
  } = new OrderSummaryService(responses);
  const { orderStatusFromGOC } = (yield select(getStatuses)) as { orderStatusFromGOC: string };
  const orderStatus = orderStatusFromGOC || responses[0].OrderStatus;
  const products = getSortedProducts();
  const { engineerAppointmentsMilestones, BBProducts, CVEProducts, ...appointmentsInfo } = getProductsInformation();
  const orderStatusFromOrderSummary = getOrderSummaryStatus();
  const serviceActivationMilestones = getServiceActivationMilestones(orderStatus, CVEProducts, BBProducts);
  const orderDescription = getOrderDescription(products);
  const orderStatuses = getSubOrdersStatuses();
  const orderPlacedMilestones = getOrderPlacedMilestone(placedOn);
  const billingMilestone = getBillingMilestone(orderStatus);
  const hasHighDemandEngineerVisitsBanner = getHighDemandEngineerVisitsBanner(appointmentsInfo.engineerAppointments);

  yield put(
    fetchOrderSummarySucceed({
      products,
      appointmentsInfo,
      orderDescription,
      isCloudVoiceOrder: !!CVEProducts.length,
      isCVESwitchedOn: CVEProducts.some(
        (product: Record<string, unknown>) =>
          !isDateInFuture(product.estimatedCompletionDate as string),
      ),
      isBroadbandOrder: !!BBProducts.length,
      hasHighDemandEngineerVisitsBanner,
    }),
  );

  yield put(
    addMilestone([
      orderPlacedMilestones,
      billingMilestone,
      ...engineerAppointmentsMilestones,
      ...serviceActivationMilestones,
    ]),
  );

  yield put(setOrderStatusFromOrderSummary({ orderStatusFromOrderSummary, orderStatuses }));
}

export function* orderSummaryRequest(): Generator {
  const {
    orderSummary: { postcode, ordersNumbers, placedOn, isGOCFetched },
    caching: { cachedOrderSummaryResponse },
  } = (yield select((state: unknown) => state)) as {
    orderSummary: { postcode: string; ordersNumbers: string[]; placedOn: string; isGOCFetched: boolean };
    caching: { cachedOrderSummaryResponse?: transformedResponse.OrderSummary[] };
  };
  try {
    const apiCalls = ordersNumbers.map((orderId: string) => call(getOrderSummary, orderId, postcode));
    let responses = (yield all(apiCalls)) as transformedResponse.OrderSummary[];

    responses = cachedOrderSummaryResponse ? [...responses, ...cachedOrderSummaryResponse] : responses;

    if (!isGOCFetched) {
      const { groupId } = responses[0];

      if (groupId) {
        yield put(
          setCachedDataToStore({
            orderId: ordersNumbers,
            cachedOrderSummaryResponse: responses,
          }),
        );
        yield put(
          setDataToStore({
            orderReference: groupId,
            postcode,
          }),
        );
        yield put(fetchGroupOrder());

        yield cancel();
      }
    }
    yield putOrderSummaryDataToStore(responses, placedOn);
  } catch (error) {
    const { message } = error as { message: string };
    yield put(
      setAPIFailed({
        isGeneralAPIFailed: true,
        isFetching: false,
        errorMessage: message,
      }),
    );

    console.error('Order Summary Request:', message);
  }
}
